import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Eye, EyeOff, Lock, Mail, Building2, AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginScreenProps {
  onForgotPassword: () => void;
  onLoginSuccess: (user: any) => void;
}

export function LoginScreen({ onForgotPassword, onLoginSuccess }: LoginScreenProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuthStore();


  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<LoginFormData>({
    defaultValues: {
      rememberMe: false
    }
  });

  const rememberMe = watch('rememberMe');


  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Store authentication data with normalized email
      await login({
        email: data.email.toLowerCase().trim(), // Normalize email to lowercase
        password: data.password,
      });

      // Call success callback with user data
      const userData = {
        id: '1',
        email: data.email,
        name: 'User',
        role: 'user'
      };
      
      onLoginSuccess(userData);
    } catch (err: any) {
      // Handle authentication errors with specific messaging
      let errorMessage = 'Login failed';
      
      if (err.response?.status === 401) {
        // Authentication failed - wrong email or password
        errorMessage = 'Oops! The email or password you entered is incorrect. Please double-check and try again.';
      } else if (err.response?.data?.detail) {
        // Use the specific error message from the server
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        // Use the error message from the exception
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background with gradient and patterns */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="absolute inset-0 opacity-40">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23001F54' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>
      </div>

      {/* Floating elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-blue-200/20 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute top-40 right-20 w-32 h-32 bg-purple-200/20 rounded-full blur-xl animate-pulse delay-1000"></div>
      <div className="absolute bottom-20 left-20 w-24 h-24 bg-indigo-200/20 rounded-full blur-xl animate-pulse delay-2000"></div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Company Branding */}
          <div className="text-center mb-6 animate-fade-in">
            <div className="inline-flex items-center justify-center w-28 h-28 rounded-3xl mb-6 shadow-2xl ring-4 ring-blue-400/30 animate-pulse" style={{ backgroundColor: '#001F54' }}>
              <img 
                src="/login_logo.png" 
                alt="Aura Logo" 
                className="w-20 h-20 object-contain filter brightness-0 invert"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <Building2 className="w-16 h-16 text-white hidden" />
            </div>
          </div>

          <Card className="border-0 shadow-2xl animate-slide-in bg-white/80 backdrop-blur-sm rounded-3xl overflow-hidden">
            <CardHeader className="space-y-1 pb-6 pt-8">
            </CardHeader>
            <CardContent className="px-8 pb-8">
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit)(e);
            }} className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-200 rounded-lg p-3 text-center">
                  <p className="text-red-600 text-sm font-medium whitespace-nowrap">
                    Oops! The email or password you entered is incorrect.
                  </p>
                </div>
              )}

              <div className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-semibold text-sm flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <span>Email Address</span>
                  </Label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400 transition-colors" style={{ color: '#001F54' }} />
                    </div>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email address"
                      className="pl-12 h-14 border-2 border-gray-200 focus:ring-4 rounded-xl transition-all duration-200 bg-gray-50/50 focus:bg-white" style={{ '--tw-ring-color': 'rgba(0, 31, 84, 0.2)', '--tw-border-opacity': '1', 'border-color': '#001F54' } as React.CSSProperties}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-sm flex items-center space-x-1">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{errors.email?.message}</span>
                    </p>
                  )}
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-700 font-semibold text-sm flex items-center space-x-2">
                    <Lock className="w-4 h-4" />
                    <span>Password</span>
                  </Label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400 transition-colors" style={{ color: '#001F54' }} />
                    </div>
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      className="pl-12 pr-12 h-14 border-2 border-gray-200 focus:ring-4 rounded-xl transition-all duration-200 bg-gray-50/50 focus:bg-white" style={{ '--tw-ring-color': 'rgba(0, 31, 84, 0.2)', '--tw-border-opacity': '1', 'border-color': '#001F54' } as React.CSSProperties}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-sm flex items-center space-x-1">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{errors.password?.message}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between pt-6">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="rememberMe"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setValue('rememberMe', !!checked)}
                    className="border-2 border-gray-300" style={{ '--tw-bg-opacity': '1', 'background-color': '#001F54', 'border-color': '#001F54' } as React.CSSProperties}
                  />
                  <Label htmlFor="rememberMe" className="text-sm text-gray-600 cursor-pointer font-medium">
                    Remember me
                  </Label>
                </div>
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-medium transition-colors hover:underline" style={{ color: '#001F54' }}
                >
                  Forgot password?
                </Link>
              </div>

              {/* Login Button */}
              <div className="pt-6">
                <Button
                  type="submit"
                  className="w-full h-14 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed group" style={{ backgroundColor: '#001F54' }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span>Sign In</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </Button>
              </div>
            </form>
            
              {/* Footer */}
              <div className="text-center mt-8 pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  © 2025 AuraHub. All rights reserved.
                </p>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-gray-500 underline hover:text-gray-700">
                  Privacy Policy
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
